import React, { useEffect, useRef, useState } from "react";
import {
  gsap,
  prefersReducedMotion,
  SplitText as GSAPSplitText,
  useGSAP,
} from "./gsap";

type Tag = "h1" | "h2" | "h3" | "p" | "span";

interface SplitTextProps {
  text: string;
  tag?: Tag;
  className?: string;
  /** What moves on its own: words read calmer, chars suit a short word. */
  splitType?: "words" | "chars";
  /** Pause between two pieces, in ms. */
  stagger?: number;
  /** Seconds for one piece. */
  duration?: number;
  /** Seconds before the first piece moves. */
  delay?: number;
}

const fontsAreLoaded = () =>
  typeof document === "undefined" ||
  !document.fonts ||
  document.fonts.status === "loaded";

/**
 * SplitText from React Bits (https://reactbits.dev, ../REACT_BITS_LICENSE.md),
 * cut down to what a title needs: on mount the words rise out of a mask,
 * one after another. The scroll trigger, loops and hover springs are gone —
 * a title plays once when its page opens. Once played, the split is undone
 * and the heading is plain text again, for copying and screen readers.
 */
const SplitText: React.FC<SplitTextProps> = ({
  text,
  tag = "p",
  className,
  splitType = "words",
  stagger = 40,
  duration = 0.7,
  delay = 0,
}) => {
  const ref = useRef<HTMLElement>(null);
  const [fontsLoaded, setFontsLoaded] = useState(fontsAreLoaded);
  // Hidden only until the split is in place, so the words never flash in
  // their final place before rising.
  const [ready, setReady] = useState(false);

  // Rubik changes the word widths: split after it has loaded.
  useEffect(() => {
    if (fontsLoaded) return undefined;
    let active = true;
    document.fonts.ready.then(() => active && setFontsLoaded(true));
    return () => {
      active = false;
    };
  }, [fontsLoaded]);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el || !fontsLoaded) return undefined;
      if (prefersReducedMotion()) {
        setReady(true);
        return undefined;
      }

      const split = new GSAPSplitText(el, {
        type: splitType,
        mask: splitType,
        wordsClass: "split-word",
        charsClass: "split-char",
      });
      const pieces = splitType === "chars" ? split.chars : split.words;
      setReady(true);

      const tween = gsap.from(pieces, {
        yPercent: 110,
        opacity: 0,
        duration,
        delay,
        ease: "power4.out",
        stagger: stagger / 1000,
        onComplete: () => split.revert(),
      });

      return () => {
        tween.kill();
        split.revert();
      };
    },
    { dependencies: [text, fontsLoaded, splitType], scope: ref },
  );

  const Tag = tag as React.ElementType;
  return (
    <Tag
      key={text}
      ref={ref}
      className={className}
      style={ready ? undefined : { visibility: "hidden" }}
    >
      {text}
    </Tag>
  );
};

export default SplitText;
